import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { SegmentedControl } from '@aimsos/ui';

import { useTabPath } from '../hooks/useTabPath.js';

/**
 * The subtab switch of a profile tab whose subtabs are CHILD ROUTES — Traits /
 * Insights (CT-5715), Timeline / From notes (CT-5724). Bound to the route, never
 * to local state: the active subtab is the first segment of the profile's
 * `:tabId/*` splat, and choosing one navigates.
 *
 * Targets are absolute, built from the current path minus the splat: with
 * `v7_relativeSplatPath` a relative link would resolve against the splat route's
 * FULL path and land on `…/traits/insights`. The query string is kept.
 *
 * @param {object} props
 * @param {readonly string[]} props.subtabs
 * @param {string} props.defaultSubtab
 * @param {string} props.labelPrefix — i18n prefix; each label is `${labelPrefix}.${id}`.
 * @param {string} props.ariaLabel
 * @param {string} [props.widthClassName='w-[180px]']
 */
function RoutedSubtabSwitch({ subtabs, defaultSubtab, labelPrefix, ariaLabel, widthClassName = 'w-[180px]' }) {
  const { t } = useTranslation('unified-profiles');
  const params = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const tabPath = useTabPath();

  const active = (params['*'] ?? '').split('/')[0] || defaultSubtab;

  const options = useMemo(
    () => subtabs.map((id) => ({ value: id, label: t(`${labelPrefix}.${id}`) })),
    [subtabs, labelPrefix, t],
  );
  const handleChange = useCallback(
    (next) => navigate({ pathname: `${tabPath}/${next}`, search }),
    [navigate, tabPath, search],
  );

  return (
    <div className={`${widthClassName} shrink-0`}>
      <SegmentedControl value={active} onChange={handleChange} options={options} ariaLabel={ariaLabel} />
    </div>
  );
}

export default RoutedSubtabSwitch;
